// Agent Bundle modal — one-time display of the provision response.
//
// This is the only chance to grab these secrets:
//   - api_key         (agent → cloud heartbeats)
//   - tunnel.token    (cloudflared daemon credential)
// Both are unrecoverable after this modal is dismissed. If the operator
// loses them, they have to delete and re-provision the agent.
//
// The modal is deliberately dismissible ONLY via an explicit "I've saved
// these" confirmation. No X in the corner.

import { useState } from 'react';

function CopyField({ label, value, secret }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be blocked (insecure contexts, browser policy).
      // The value is already visible for manual copy; nothing else to do.
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">
          {label}
          {secret && <span className="ml-1 rounded bg-red-100 px-1 text-red-700">SECRET</span>}
        </label>
        <button
          onClick={copy}
          className="text-xs font-medium text-blue-600 hover:underline"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="mt-1 max-h-32 overflow-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800 break-all">
        {value}
      </div>
    </div>
  );
}

export default function AgentBundleModal({ bundle, onClose }) {
  const [confirmed, setConfirmed] = useState(false);

  const jsonString = JSON.stringify(bundle, null, 2);

  async function copyBundle() {
    try { await navigator.clipboard.writeText(jsonString); }
    catch { /* clipboard blocked */ }
  }

  function downloadBundle() {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${bundle.slug || 'verso-agent'}-bundle.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 bg-amber-50 px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">
            PBX host provisioned — save this now
          </h3>
          <p className="mt-1 text-sm text-amber-800">
            Verso cannot show these secrets again. If you lose them, delete this
            host and provision a new one. Paste the values below into the
            verso-agent installer on the PBX.
          </p>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs font-medium text-gray-500">PBX host</div>
              <div className="font-medium text-gray-900">{bundle.name}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Hostname</div>
              <div className="font-mono text-xs text-gray-700">{bundle.hostname}</div>
            </div>
          </div>

          <CopyField label="Cloud URL"       value={bundle.cloud_url} />
          <CopyField label="Agent API key"   value={bundle.api_key}          secret />
          <CopyField label="Tunnel token"    value={bundle.tunnel?.token}    secret />

          <details className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Full bundle (JSON)
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto text-xs text-gray-700">
              {jsonString}
            </pre>
            <div className="mt-2 flex gap-2">
              <button
                onClick={copyBundle}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-white"
              >
                Copy JSON
              </button>
              <button
                onClick={downloadBundle}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-white"
              >
                Download .json
              </button>
            </div>
          </details>

          <label className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded border-blue-300"
            />
            I&rsquo;ve saved the API key and tunnel token somewhere safe.
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
          <button
            onClick={onClose}
            disabled={!confirmed}
            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
